import {
  Tenant,
  Landlord,
  Property,
  Payment,
  Notification,
  VerificationRequest,
  RiskScore,
  PayrollProvider,
  Employer,
  EmployerAuthorization,
  MaintenanceRequest,
  ServiceProvider,
  LeaseAgreement,
  ApplicationTemplate,
  RentalApplication,
  RentCertificate,
  ReliabilityScore,
  FlexRequest,
  PortfolioStats,
} from '../types'

export const MOCK_TENANT: Tenant = {
  id: 't1',
  name: 'Chioma Adebayo',
  email: 'chioma@example.com',
  role: 'tenant',
  phone: '+234 801 234 5678',
  isVerified: true,
  rentAmount: 150000,
  paymentFrequency: 'monthly',
  nextPaymentDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
  leaseStartDate: '2023-01-01',
  leaseEndDate: '2024-01-01',
  propertyId: 'p1',
  onboardingStep: 4,
  documents: {
    id: true,
    agreement: true,
    bankStatement: true,
  },
  creditScore: 750,
  riskScore: {
    score: 15,
    level: 'Low',
    confidence: 95,
    factors: {
      incomeStability: 90,
      paymentHistory: 95,
      employmentType: 'Permanent',
      creditScore: 750,
    },
    trend: [
      { month: 'May', score: 25 },
      { month: 'Jun', score: 22 },
      { month: 'Jul', score: 20 },
      { month: 'Aug', score: 18 },
      { month: 'Sep', score: 16 },
      { month: 'Oct', score: 15 },
    ],
    recommendations: [
      'Maintain current payment schedule',
      'Keep credit utilization low',
    ],
  },
  reliabilityScore: {
    score: 85,
    tier: 'Excellent',
    history: [
      { month: 'May', score: 80 },
      { month: 'Jun', score: 82 },
      { month: 'Jul', score: 83 },
      { month: 'Aug', score: 84 },
      { month: 'Sep', score: 85 },
      { month: 'Oct', score: 85 },
    ],
    metrics: {
      onTimePercentage: 98,
      averagePaymentTime: 1.2,
      totalPaid: 1800000,
      monthsOnPlatform: 12,
      currentStreak: 12,
    },
  },
}

export const MOCK_LANDLORD: Landlord = {
  id: 'l1',
  name: 'Tunde Bakare',
  email: 'tunde@example.com',
  role: 'landlord',
  isVerified: true,
  properties: ['p1', 'p2'],
  totalTenants: 5,
  totalRevenue: 4500000,
  verificationStatus: 'verified',
  trustScore: 92,
}

export const MOCK_EMPLOYER: Employer = {
  id: 'e1',
  name: 'TechCorp Nigeria Ltd',
  email: 'hr@techcorp.ng',
  role: 'employer',
  isVerified: true,
  companyName: 'TechCorp Nigeria Ltd',
  industry: 'Technology',
  employeeCount: 150,
  activeDeductions: 12,
  pendingRequests: 3,
}

export const MOCK_PROPERTIES: Property[] = [
  {
    id: 'p1',
    landlordId: 'l1',
    name: 'Sunrise Apartments, Flat 4B',
    address: '12 Admiralty Way, Lekki Phase 1',
    city: 'Lagos',
    state: 'Lagos',
    rentAmount: 150000,
    tenantId: 't1',
    status: 'occupied',
  },
  {
    id: 'p2',
    landlordId: 'l1',
    name: 'Green Valley Estate, House 10',
    address: '45 Gwarinpa Estate',
    city: 'Abuja',
    state: 'FCT',
    rentAmount: 250000,
    status: 'vacant',
  },
  {
    id: 'p3',
    landlordId: 'l1',
    name: 'Blue Water Residence, Apt 202',
    address: '8 Victoria Arobieke St, Lekki',
    city: 'Lagos',
    state: 'Lagos',
    rentAmount: 300000,
    status: 'maintenance',
  },
]

export const MOCK_PAYMENTS: Payment[] = [
  {
    id: 'pay1',
    tenantId: 't1',
    propertyId: 'p1',
    amount: 150000,
    date: '2023-10-01',
    status: 'paid',
    type: 'rent',
  },
  {
    id: 'pay2',
    tenantId: 't1',
    propertyId: 'p1',
    amount: 150000,
    date: '2023-09-01',
    status: 'paid',
    type: 'rent',
  },
  {
    id: 'pay3',
    tenantId: 't1',
    propertyId: 'p1',
    amount: 150000,
    date: '2023-11-01',
    status: 'pending',
    type: 'rent',
  },
  {
    id: 'pay4',
    tenantId: 't1',
    propertyId: 'p1',
    amount: 150000,
    date: '2023-08-01',
    status: 'paid',
    type: 'rent',
  },
  {
    id: 'pay5',
    tenantId: 't1',
    propertyId: 'p1',
    amount: 150000,
    date: '2023-07-01',
    status: 'paid',
    type: 'rent',
  },
]

export const MOCK_NOTIFICATIONS: Notification[] = [
  {
    id: 'n1',
    userId: 't1',
    title: 'Rent Due Soon',
    message: 'Your rent of ₦150,000 is due in 7 days.',
    type: 'warning',
    isRead: false,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'n2',
    userId: 't1',
    title: 'Payment Successful',
    message: 'We received your payment for October rent.',
    type: 'success',
    isRead: true,
    createdAt: '2023-10-01T10:00:00Z',
  },
]

export const MOCK_VERIFICATIONS: VerificationRequest[] = [
  {
    id: 'v1',
    tenantId: 't2',
    tenantName: 'Emeka Okafor',
    status: 'pending',
    submittedAt: new Date().toISOString(),
    documents: ['ID Card', 'Tenancy Agreement', 'Bank Statement'],
    riskScore: 15,
  },
  {
    id: 'v2',
    tenantId: 't3',
    tenantName: 'Sarah Idahosa',
    status: 'approved',
    submittedAt: '2023-10-15T09:30:00Z',
    documents: ['Passport', 'Tenancy Agreement'],
    riskScore: 5,
  },
  {
    id: 'v3',
    tenantId: 't4',
    tenantName: 'David Okon',
    status: 'rejected',
    submittedAt: '2023-10-10T14:20:00Z',
    documents: ['ID Card'],
    riskScore: 65,
  },
  {
    id: 'v4',
    tenantId: 't5',
    tenantName: 'Amina Yusuf',
    status: 'pending',
    submittedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    documents: ['ID Card', 'Tenancy Agreement'],
    riskScore: 25,
  },
]

export const MOCK_ANALYTICS = {
  paymentCompliance: [
    { month: 'Jan', rate: 92 },
    { month: 'Feb', rate: 94 },
    { month: 'Mar', rate: 91 },
    { month: 'Apr', rate: 95 },
    { month: 'May', rate: 96 },
    { month: 'Jun', rate: 93 },
  ],
  tenantRetention: [
    { month: 'Jan', rate: 88 },
    { month: 'Feb', rate: 89 },
    { month: 'Mar', rate: 89 },
    { month: 'Apr', rate: 90 },
    { month: 'May', rate: 91 },
    { month: 'Jun', rate: 92 },
  ],
  riskDistribution: [
    { name: 'Low Risk', value: 65, color: '#10B981' },
    { name: 'Medium Risk', value: 25, color: '#F59E0B' },
    { name: 'High Risk', value: 10, color: '#EF4444' },
  ],
  revenue: [
    { month: 'Jan', amount: 12000000 },
    { month: 'Feb', amount: 13500000 },
    { month: 'Mar', amount: 12800000 },
    { month: 'Apr', amount: 14200000 },
    { month: 'May', amount: 15100000 },
    { month: 'Jun', amount: 16500000 },
  ],
}

export const MOCK_ACTIVITIES = [
  {
    id: 'a1',
    type: 'payment',
    title: 'Rent Payment Successful',
    description: 'Payment of ₦150,000 for October rent processed.',
    timestamp: '2 hours ago',
    icon: 'check',
  },
  {
    id: 'a2',
    type: 'document',
    title: 'Document Verified',
    description: 'Your ID Card has been verified by admin.',
    timestamp: '1 day ago',
    icon: 'file',
  },
  {
    id: 'a3',
    type: 'message',
    title: 'New Message from Support',
    description: 'We have resolved your ticket #4291.',
    timestamp: '2 days ago',
    icon: 'message',
  },
  {
    id: 'a4',
    type: 'alert',
    title: 'Rent Due Reminder',
    description: 'Your rent is due in 3 days.',
    timestamp: '4 days ago',
    icon: 'alert',
  },
]

export const MOCK_DOCUMENTS = [
  {
    id: 'd1',
    name: 'Tenancy Agreement 2023.pdf',
    type: 'pdf',
    size: '2.4 MB',
    date: 'Jan 15, 2023',
    status: 'verified',
    category: 'Agreements',
  },
  {
    id: 'd2',
    name: 'International Passport.jpg',
    type: 'image',
    size: '1.8 MB',
    date: 'Jan 10, 2023',
    status: 'verified',
    category: 'ID',
  },
  {
    id: 'd3',
    name: 'Oct Rent Receipt.pdf',
    type: 'pdf',
    size: '0.5 MB',
    date: 'Oct 01, 2023',
    status: 'verified',
    category: 'Receipts',
  },
  {
    id: 'd4',
    name: 'Bank Statement.pdf',
    type: 'pdf',
    size: '4.2 MB',
    date: 'Jan 12, 2023',
    status: 'expired',
    category: 'Statements',
  },
]

export const MOCK_FAQ = [
  {
    id: 'f1',
    question: 'How does monthly rent payment work?',
    answer:
      'Renta allows you to pay your rent in monthly installments instead of a bulk annual payment. We pay your landlord the full amount upfront (or as agreed) and you pay us back monthly with a small service fee.',
    category: 'Payments',
  },
  {
    id: 'f2',
    question: 'What documents do I need to sign up?',
    answer:
      "You need a valid government-issued ID (Passport, NIN, or Driver's License), proof of income (bank statement), and your current tenancy agreement.",
    category: 'Verification',
  },
  {
    id: 'f3',
    question: 'Is my data secure?',
    answer:
      'Yes, we use bank-grade encryption for all your data. We are NDPR compliant and do not share your personal information with third parties without your consent.',
    category: 'Security',
  },
  {
    id: 'f4',
    question: 'Can I pay early?',
    answer:
      'Absolutely! You can pay your monthly rent anytime before the due date. There are no penalties for early payment.',
    category: 'Payments',
  },
]

export const MOCK_CHAT_MESSAGES = [
  {
    id: 'm1',
    text: 'Hello! How can I help you today?',
    sender: 'bot',
    timestamp: 'now',
  },
]

export const MOCK_PAYROLL_PROVIDERS: PayrollProvider[] = [
  {
    id: 'seamlesshr',
    name: 'SeamlessHR',
    logo: 'https://via.placeholder.com/40?text=SHR',
    description: 'Leading HR & Payroll software in Africa',
    status: 'active',
  },
  {
    id: 'paystack',
    name: 'Paystack Payroll',
    logo: 'https://via.placeholder.com/40?text=PP',
    description: 'Automated payroll for modern businesses',
    status: 'active',
  },
  {
    id: 'workpay',
    name: 'Workpay',
    logo: 'https://via.placeholder.com/40?text=WP',
    description: 'HR & Payroll management simplified',
    status: 'active',
  },
  {
    id: 'zoho',
    name: 'Zoho Payroll',
    logo: 'https://via.placeholder.com/40?text=ZP',
    description: 'Tax-compliant payroll software',
    status: 'active',
  },
]

export const MOCK_AUTHORIZATIONS: EmployerAuthorization[] = [
  {
    id: 'auth1',
    tenantId: 't1',
    employerId: 'e1',
    status: 'pending',
    requestedAt: new Date().toISOString(),
    rentAmount: 150000,
    employeeName: 'Chioma Adebayo',
    employeeId: 'EMP001',
  },
  {
    id: 'auth2',
    tenantId: 't2',
    employerId: 'e1',
    status: 'approved',
    requestedAt: '2023-10-01T10:00:00Z',
    approvedAt: '2023-10-02T14:00:00Z',
    rentAmount: 200000,
    employeeName: 'Emeka Okafor',
    employeeId: 'EMP002',
  },
]

export const MOCK_MAINTENANCE_REQUESTS: MaintenanceRequest[] = [
  {
    id: 'mr1',
    tenantId: 't1',
    propertyId: 'p1',
    category: 'plumbing',
    description:
      'Kitchen sink is leaking and causing water damage to the cabinet.',
    urgency: 'high',
    status: 'submitted',
    photos: ['https://via.placeholder.com/300'],
    submittedAt: new Date().toISOString(),
  },
  {
    id: 'mr2',
    tenantId: 't1',
    propertyId: 'p1',
    category: 'electrical',
    description: 'Bedroom light switch is sparking when turned on.',
    urgency: 'emergency',
    status: 'in_progress',
    photos: ['https://via.placeholder.com/300'],
    submittedAt: '2023-10-20T10:00:00Z',
    assignedTo: 'sp1',
  },
  {
    id: 'mr3',
    tenantId: 't1',
    propertyId: 'p1',
    category: 'hvac',
    description: 'The living room AC unit is running but not cooling the room.',
    urgency: 'medium',
    status: 'completed',
    photos: [],
    submittedAt: '2023-10-15T10:00:00Z',
    assignedTo: 'sp1',
  },
]

export const MOCK_SERVICE_PROVIDERS: ServiceProvider[] = [
  {
    id: 'sp1',
    name: 'FixIt Pro Services',
    specialty: 'Electrical',
    rating: 4.8,
    phone: '+234 800 111 2222',
    email: 'contact@fixitpro.ng',
  },
  {
    id: 'sp2',
    name: 'Lagos Plumbers',
    specialty: 'Plumbing',
    rating: 4.5,
    phone: '+234 800 333 4444',
    email: 'help@lagosplumbers.ng',
  },
]

export const MOCK_LEASES: LeaseAgreement[] = [
  {
    id: 'la1',
    propertyId: 'p1',
    landlordId: 'l1',
    startDate: '2023-01-01',
    endDate: '2024-01-01',
    rentAmount: 150000,
    status: 'active',
    clauses: ['No pets', 'No smoking'],
    createdAt: '2022-12-15T10:00:00Z',
  },
]

export const MOCK_APPLICATION_TEMPLATES: ApplicationTemplate[] = [
  {
    id: 'at1',
    landlordId: 'l1',
    name: 'Standard Residential Application',
    createdAt: '2023-01-01T10:00:00Z',
    fields: [
      { id: 'f1', type: 'text', label: 'Full Name', required: true },
      { id: 'f2', type: 'text', label: 'Current Employer', required: true },
      { id: 'f3', type: 'number', label: 'Monthly Income', required: true },
      { id: 'f4', type: 'file', label: 'ID Document', required: true },
    ],
  },
]

export const MOCK_CERTIFICATES: RentCertificate[] = [
  {
    id: 'rc1',
    tenantId: 't1',
    tenantName: 'Chioma Adebayo',
    propertyAddress: '12 Admiralty Way, Lekki Phase 1',
    coverageStartDate: '2023-01-01',
    coverageEndDate: '2024-01-01',
    status: 'active',
    qrCodeUrl: 'https://via.placeholder.com/150?text=QR+Code',
    issuedAt: '2023-01-01T10:00:00Z',
  },
]

export const MOCK_FLEX_REQUESTS: FlexRequest[] = [
  {
    id: 'fr1',
    tenantId: 't1',
    type: 'skip',
    status: 'pending',
    details: { reason: 'Medical Emergency', month: 'Nov 2023' },
    requestedAt: new Date().toISOString(),
  },
]

export const MOCK_PORTFOLIO_STATS: PortfolioStats = {
  totalProperties: 5,
  totalTenants: 4,
  totalRevenue: 12500000,
  occupancyRate: 80,
  avgRent: 2500000,
  paymentCompliance: 95,
}

// Mock Rent Requests
export const MOCK_RENT_REQUESTS = [
  {
    id: 'rr1',
    tenantId: 't2',
    tenantName: 'Chioma Adebayo',
    propertyId: 'p1',
    propertyAddress: '12 Admiralty Way, Lekki Phase 1, Lagos',
    landlordId: 'l1',
    principal: 2500000,
    duration: 12,
    monthlyInstallment: 312500,
    financierInterest: 625000,
    rentaFee: 625000,
    totalRepayment: 3750000,
    tenantRiskScore: {
      score: 15,
      level: 'Low' as const,
      confidence: 95,
      factors: {
        incomeStability: 90,
        paymentHistory: 95,
        employmentType: 'Permanent' as const,
        creditScore: 750,
      },
      trend: [],
      recommendations: [],
    },
    tenantMonthlyIncome: 850000,
    rentToIncomeRatio: 36.8,
    isEligible: true,
    status: 'pending' as const,
    fundingStatus: 'unfunded' as const,
    fundedAmount: 0,
    fundedBy: [],
    repaymentSchedule: [],
    nextPaymentDate: new Date(
      Date.now() + 30 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    paidInstallments: 0,
    remainingBalance: 3750000,
    autoDebitEnabled: true,
    salaryAccountLinked: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'rr2',
    tenantId: 't3',
    tenantName: 'Emmanuel Okon',
    propertyId: 'p2',
    propertyAddress: '45 Gwarinpa Estate, Abuja',
    landlordId: 'l1',
    principal: 1800000,
    duration: 12,
    monthlyInstallment: 225000,
    financierInterest: 450000,
    rentaFee: 450000,
    totalRepayment: 2700000,
    tenantRiskScore: {
      score: 35,
      level: 'Medium' as const,
      confidence: 85,
      factors: {
        incomeStability: 75,
        paymentHistory: 80,
        employmentType: 'Contract' as const,
        creditScore: 680,
      },
      trend: [],
      recommendations: [],
    },
    tenantMonthlyIncome: 600000,
    rentToIncomeRatio: 37.5,
    isEligible: true,
    status: 'pending' as const,
    fundingStatus: 'partially_funded' as const,
    fundedAmount: 900000,
    fundedBy: [],
    repaymentSchedule: [],
    nextPaymentDate: new Date(
      Date.now() + 30 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    paidInstallments: 0,
    remainingBalance: 2700000,
    autoDebitEnabled: true,
    salaryAccountLinked: true,
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'rr3',
    tenantId: 't4',
    tenantName: 'Sarah Johnson',
    propertyId: 'p3',
    propertyAddress: '8 Victoria Arobieke St, Lekki, Lagos',
    landlordId: 'l1',
    principal: 3200000,
    duration: 12,
    monthlyInstallment: 400000,
    financierInterest: 800000,
    rentaFee: 800000,
    totalRepayment: 4800000,
    tenantRiskScore: {
      score: 10,
      level: 'Low' as const,
      confidence: 98,
      factors: {
        incomeStability: 95,
        paymentHistory: 98,
        employmentType: 'Permanent' as const,
        creditScore: 820,
      },
      trend: [],
      recommendations: [],
    },
    tenantMonthlyIncome: 1200000,
    rentToIncomeRatio: 33.3,
    isEligible: true,
    status: 'pending' as const,
    fundingStatus: 'unfunded' as const,
    fundedAmount: 0,
    fundedBy: [],
    repaymentSchedule: [],
    nextPaymentDate: new Date(
      Date.now() + 30 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    paidInstallments: 0,
    remainingBalance: 4800000,
    autoDebitEnabled: true,
    salaryAccountLinked: true,
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
]
